/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  Alert,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/system';
import { useCreateFileUrl } from '_api/index';
import { PresignedUrl } from '_api/types';
import { StyledFormLabel, StyledOutlinedInput } from 'components/atoms';
import {
  FileFieldProps,
  withGridItemCSS,
  withWrapperCSS,
} from 'components/organisms/forms/form-builder/types';
import { noop } from 'lodash';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { IoMdClose, IoMdPhotos } from 'react-icons/io';
import { IoDocumentsSharp } from 'react-icons/io5';
import { uploadFile, md5Base64, generateFileChallenge } from 'utils/media';

import { FileExtention, FILE_EXTENSION_TO_MIME_MAP } from '../types';

const StyledFileInput = styled('input')({
  cursor: 'pointer',
  display: 'none',
});

export const FileInputField: FC<FileFieldProps & withGridItemCSS & withWrapperCSS> = ({
  acceptFileType = 'image/*',
  disabled = false,
  error,
  fieldKey,
  initValue = '',
  gridItemCss,
  label = '',
  onChange = noop,
  isUploadingFile = noop,
  labelCss,
  required = false,
  wrapperCss,
  xs = 12,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileNameValue, setFileNameValue] = useState(initValue);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [extension, setExtention] = useState<FileExtention | undefined>(undefined);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => isUploadingFile(uploadingFile), [uploadingFile]);

  const { mutateAsync: createUploadUrl } = useCreateFileUrl();

  const getFileType = (ext: string) => {
    const fileType = ext.split('.').pop();
    const mimeType = FILE_EXTENSION_TO_MIME_MAP[fileType || ''];
    if (mimeType) {
      setExtention(mimeType);
    } else {
      onError({ message: 'Unsupported file type' }, () => removeFile());
    }
  };

  const onSuccess = (data: PresignedUrl | null) => {
    if (data) {
      onChange(data.key, fieldKey);
    }
    setUploadingFile(false);
  };

  const onError = (err: any, onClear?: () => void) => {
    setShowError(true);
    setErrorMessage(err.message);
    setTimeout(() => {
      setShowError(false);
      if (onClear) {
        onClear();
      }
    }, 3500);
  };

  useEffect(() => {
    const upload = async () => {
      if (!file || !extension) {
        return null;
      }

      setUploadingFile(true);
      const checksum = await md5Base64(file);
      const challenge = await generateFileChallenge(file);
      const uploadUrl = await createUploadUrl({
        filename: file.name,
        contentType: extension,
        checksum,
        challenge,
      });

      await uploadFile(uploadUrl.url, file, extension);

      return uploadUrl;
    };

    if (file && extension !== undefined) {
      setUploadingFile(true);
      upload().then(onSuccess).catch(onError);
    }
  }, [file, extension]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputFile = event.target.files ? event.target.files[0] : null;
    if (inputFile) {
      getFileType(inputFile.name);
      setFile(inputFile);
      setFileNameValue(inputFile.name);
      onChange(inputFile.name, fieldKey);
    }
  };

  const removeFile = () => {
    setFile(null);
    setExtention(undefined);
    setFileNameValue('');
    onChange('', fieldKey);
  };

  const handleInputClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    if (fileNameValue === '') fileInputRef.current?.click();
  };

  return (
    <Grid item xs={xs} style={gridItemCss}>
      <FormControl
        error={!!error}
        required={required}
        style={{ width: '100%', ...wrapperCss }}
      >
        <StyledFormLabel error={!!error} style={labelCss}>
          {label} {required ? '*' : null}
        </StyledFormLabel>
        <StyledFileInput
          accept={acceptFileType}
          id={`file-input-${fieldKey}`}
          multiple
          type="file"
          onChange={handleFileChange}
          ref={fileInputRef}
          width={'100%'}
        />
        <label htmlFor={`file-input-${fieldKey}`} style={{ cursor: 'pointer' }}>
          <StyledOutlinedInput
            id="file-input"
            type="type"
            value={fileNameValue}
            onChange={noop}
            placeholder="Max size 1MB"
            error={file != null && !extension}
            onClick={handleInputClick}
            sx={{
              width: '100%',
              '& .MuiInputBase-input': {
                caretColor: 'transparent',
                cursor: 'pointer',
              },
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  disabled={disabled || uploadingFile}
                  aria-label="upload file"
                  component="span"
                  edge="end"
                >
                  {uploadingFile ? (
                    <CircularProgress />
                  ) : fileNameValue !== '' ? (
                    <IoMdClose onClick={removeFile} />
                  ) : acceptFileType.startsWith('image') ? (
                    <IoMdPhotos />
                  ) : (
                    <IoDocumentsSharp />
                  )}
                </IconButton>
              </InputAdornment>
            }
          />
        </label>
      </FormControl>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        open={showError}
      >
        <Alert onClose={() => setShowError(false)} severity="error" variant="filled">
          Failed to upload file
          <br />
          {errorMessage}
          <br />
          Resolve issues and try again.
        </Alert>
      </Snackbar>
    </Grid>
  );
};
